import React, { useState, useEffect } from "react";
import { db } from "../firebase/firebase-config";
import { collection, addDoc, getDocs } from "firebase/firestore";
import { useParams } from "react-router-dom";

interface FormData {
    patientId: string;
    date: string;
    time: string;
    hasPain: string;
    painLevel: string;
    hasPharmacologicalIntervention: string;
    pharmacologicalIntervention: { time: string; action: string }[];
    nonPharmacologicalIntervention: string;
    syariahIntervention: string;
    tanggalKajiUlang: string;
}

interface SyariahIntervention {
    id: string;
    range: string;
    intervention: string;
}

const InterventionForm: React.FC<{ patientId?: string }> = ({ patientId: propPatientId }) => {
    const { id: paramPatientId } = useParams<{ id: string }>();
    const patientId = propPatientId || paramPatientId || "";

    const [formData, setFormData] = useState<FormData>({
        patientId: patientId,
        date: "",
        time: "",
        hasPain: "no",
        painLevel: "",
        hasPharmacologicalIntervention: "no",
        pharmacologicalIntervention: [],
        nonPharmacologicalIntervention: "",
        syariahIntervention: "",
        tanggalKajiUlang: "",
    });

    useEffect(() => {
        if (patientId) {
            setFormData((prevData) => ({ ...prevData, patientId }));
        }
    }, [patientId]);

    const [syariahInterventions, setSyariahInterventions] = useState<SyariahIntervention[]>([]);

    useEffect(() => {
        const fetchSyariahInterventions = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "syariah_interventions"));
                const data = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as SyariahIntervention[];
                setSyariahInterventions(data);
            } catch (error) {
                console.error("Error fetching Syariah Interventions:", error);
            }
        };

        fetchSyariahInterventions();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prevData) => {
            const updatedData = { ...prevData, [name]: value };

            if (name === "painLevel") {
                const painLevelNum = Number(value); // Pastikan ini angka
                if (!isNaN(painLevelNum)) {
                    const matchedIntervention = syariahInterventions.find((intervention) => {
                        const [min, max] = intervention.range.split("-").map(Number);
                        return painLevelNum >= min && painLevelNum <= max;
                    });
                    updatedData.syariahIntervention = matchedIntervention ? matchedIntervention.intervention : "";
                }
            }

            return updatedData;
        });
    };

    const handlePharmaChange = (index: number, field: "time" | "action", value: string) => {
        setFormData((prevData) => {
            const updatedPharma = [...prevData.pharmacologicalIntervention];
            updatedPharma[index] = { ...updatedPharma[index], [field]: value };
            return { ...prevData, pharmacologicalIntervention: updatedPharma };
        });
    };

    const addPharmaAction = () => {
        setFormData((prevData) => ({
            ...prevData,
            pharmacologicalIntervention: [...prevData.pharmacologicalIntervention, { time: "", action: "" }],
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.patientId) {
            alert("Patient ID is missing!");
            return;
        }

        try {
            await addDoc(collection(db, "intervention_records"), formData);
            alert("Data berhasil disimpan!");
        } catch (error) {
            console.error("Error saving data: ", error);
        }
    };

    return (
        <div className="max-w-lg mx-auto p-6 bg-white shadow-lg rounded-lg">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Intervention Form</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                <input type="hidden" name="patientId" value={formData.patientId} />

                <div>
                    <label className="block text-sm font-medium text-gray-700">Date</label>
                    <input type="date" name="date" value={formData.date} onChange={handleChange} className="w-full p-2 border rounded-md" required />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Time</label>
                    <input type="time" name="time" value={formData.time} onChange={handleChange} className="w-full p-2 border rounded-md" required />
                </div>

                {/* Pain Section */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Do you have pain?</label>
                    <div className="flex space-x-4">
                        <label>
                            <input type="radio" name="hasPain" value="yes" checked={formData.hasPain === "yes"} onChange={handleChange} />
                            Yes
                        </label>
                        <label>
                            <input type="radio" name="hasPain" value="no" checked={formData.hasPain === "no"} onChange={handleChange} />
                            No
                        </label>
                    </div>
                </div>

                {/* Pain Level */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Pain Level</label>
                    <input type="number" name="painLevel" value={formData.painLevel} onChange={handleChange} className="w-full p-2 border rounded-md" min="0" max="10" required />

                </div>

                {/* Pharmacological Intervention */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Pharmacological Intervention?</label>
                    <div className="flex space-x-4">
                        <label>
                            <input type="radio" name="hasPharmacologicalIntervention" value="yes" checked={formData.hasPharmacologicalIntervention === "yes"} onChange={handleChange} />
                            Yes
                        </label>
                        <label>
                            <input type="radio" name="hasPharmacologicalIntervention" value="no" checked={formData.hasPharmacologicalIntervention === "no"} onChange={handleChange} />
                            No
                        </label>
                    </div>
                </div>

                {formData.hasPharmacologicalIntervention === "yes" && (
                    <div>
                        <table className="border-collapse border border-black w-full">
                            <thead>
                            <tr>
                                <th className="border border-black px-2">Jam</th>
                                <th className="border border-black px-2">Tindakan</th>
                            </tr>
                            </thead>
                            <tbody>
                            {formData.pharmacologicalIntervention.map((entry, index) => (
                                <tr key={index}>
                                    <td className="border border-black">
                                        <input type="time" value={entry.time} onChange={(e) => handlePharmaChange(index, "time", e.target.value)} className="w-full p-2" required />
                                    </td>
                                    <td className="border border-black">
                                        <input type="text" value={entry.action} onChange={(e) => handlePharmaChange(index, "action", e.target.value)} className="w-full p-2" required />
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        <button type="button" onClick={addPharmaAction} className="mt-2 bg-blue-500 text-white p-2 rounded">Tambah Tindakan</button>
                    </div>
                )}


                <label>Syariah Intervention:</label>
                {/*<input type="text" name="syariahIntervention" value={formData.syariahIntervention} readOnly />*/}

                <textarea
                    name="syariahIntervention"
                    value={formData.syariahIntervention}
                    readOnly
                    className="w-full border border-gray-300 p-2 rounded-md bg-gray-100"
                />

                {/*<input type="date" name="tanggalKajiUlang" value={formData.tanggalKajiUlang} onChange={handleChange} />*/}
                <input
                    type="date"
                    name="tanggalKajiUlang"
                    value={formData.tanggalKajiUlang}
                    onChange={handleChange}
                />
                <button type="submit" className="w-full bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700">Submit</button>
            </form>
        </div>
    );
};

export default InterventionForm;
